import { useState, useEffect } from "react";
import { usePageMeta } from "@/lib/seo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Gift } from "lucide-react";
import api from "@/lib/api";
import { PROMO_STORAGE_KEY } from "@/components/PromoCodeField";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BIZ_TYPES = ["auto_repair", "collision", "dealership", "contractor", "service", "other"];

export default function RegisterPage() {
  usePageMeta("Create Account");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "", business_type: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchParams] = useSearchParams();
  const [referral, setReferral] = useState(null);

  useEffect(() => {
    const fromUrl = searchParams.get("ref");
    const ref = fromUrl || localStorage.getItem("sa_referral_code");
    if (!ref) return;
    api.get(`/api/referral/${ref}${fromUrl ? "?track=true" : ""}`).then(({ data }) => {
      setReferral(data);
      localStorage.setItem("sa_referral_code", data.code);
      localStorage.setItem(PROMO_STORAGE_KEY, data.promo_code);
    }).catch(() => localStorage.removeItem("sa_referral_code"));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register({ ...form, referral_code: referral?.code || "" });
      localStorage.removeItem("sa_referral_code");
      toast.success("Account created! Welcome to ShopAdvisor.");
      navigate("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join(" ") : "Registration failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center pt-16 pb-8 px-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="overflow-hidden" style={{ height: '60px', width: '260px' }}>
              <img src="https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png" alt="ShopAdvisor" className="w-full h-auto" style={{ marginTop: '-38%', marginBottom: '-38%' }} />
            </div>
          </div>
          <h1 className="text-3xl font-black text-slate-900">Get Started</h1>
          <p className="text-slate-500 text-sm mt-1">Create your ShopAdvisor account</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
          <button onClick={handleGoogleLogin} className="w-full flex items-center justify-center gap-3 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-medium py-3 rounded-lg transition-colors mb-6" data-testid="google-register-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Sign up with Google
          </button>
          <div className="relative mb-6">
            <Separator className="bg-slate-200" />
            <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-xs text-slate-400">or register with email</span>
          </div>
          {referral && (
            <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4" data-testid="register-referral-banner">
              <Gift className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">Referred by <strong>{referral.rep_name}</strong>{referral.discount_percent > 0 ? <> — you get <strong>{referral.discount_percent}% off</strong> your first plan. Code <span className="font-mono font-semibold">{referral.promo_code}</span> applies automatically at checkout.</> : "."}</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm p-3 rounded-lg" data-testid="register-error">{error}</div>}
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Full Name</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="John Smith" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="register-name-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Email</Label><Input type="email" className="bg-white border-slate-300 text-slate-900" placeholder="john@shop.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required data-testid="register-email-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Password</Label><Input type="password" className="bg-white border-slate-300 text-slate-900" placeholder="Min 6 characters" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={6} data-testid="register-password-input" /></div>
            <div><Label className="text-slate-700 text-sm mb-1.5 block">Business Name</Label><Input className="bg-white border-slate-300 text-slate-900" placeholder="Smith's Auto Repair" value={form.business_name} onChange={e => setForm({...form, business_name: e.target.value})} data-testid="register-business-input" /></div>
            <div>
              <Label className="text-slate-700 text-sm mb-1.5 block">Business Type</Label>
              <Select value={form.business_type} onValueChange={v => setForm({...form, business_type: v})}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="register-biztype-select"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {BIZ_TYPES.map(t => <SelectItem key={t} value={t} className="text-slate-900">{t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50" data-testid="register-submit-btn">
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>
          <p className="text-center text-sm text-slate-500 mt-5">
            Already have an account? <Link to="/login" className="text-blue-600 hover:text-blue-500 font-medium" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
